import { useEffect, useState } from "react";
import { Loader2, Banknote, AlertCircle, Check, ShieldAlert } from "lucide-react";
import { api } from "../../App";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

/* AdminEarningsTab
 * ----------------
 * Two stacked views inside one tab:
 *  1. Bookings earnings split — gross / Rivones fee / host payout, per booking
 *  2. Identity manual-review queue — failed Stripe Identity attempts the
 *     admin needs to approve or block
 */
const fmtMxn = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Intl.DateTimeFormat("es-MX", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    }).format(new Date(s));
  } catch (_e) { return s; }
};

const PayoutPill = ({ status, paymentStatus }) => {
  if (status === "reversed") {
    return <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-800 text-[11px] font-medium ring-1 ring-rose-200">Reembolsado</span>;
  }
  if (paymentStatus === "paid") {
    return <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 text-[11px] font-medium ring-1 ring-emerald-200">Pagado</span>;
  }
  return <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 text-[11px] font-medium ring-1 ring-amber-200">Pendiente</span>;
};

export default function AdminEarningsTab() {
  const [earnings, setEarnings] = useState(null);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null);

  const reload = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/earnings?limit=100").catch(() => ({ data: { totals: {}, items: [] } })),
      api.get("/admin/identity/manual-review").catch(() => ({ data: { items: [] } })),
    ]).then(([e, r]) => {
      setEarnings(e.data);
      setReview(r.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(reload, 0);
    return () => clearTimeout(t);
  }, []);

  const resolve = async (userId, action) => {
    setResolving(userId + action);
    try {
      await api.post(`/admin/identity/manual-review/${userId}/resolve`, { action });
      toast.success(action === "approve" ? "Usuario aprobado manualmente" : "Usuario bloqueado");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Acción falló");
    } finally {
      setResolving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  const totals = earnings?.totals || { bookings: 0, gross_mxn: 0, fees_mxn: 0, payouts_mxn: 0 };
  const items = earnings?.items || [];
  const reviewItems = review?.items || [];

  return (
    <div className="space-y-8" data-testid="admin-earnings-tab">
      {/* Header + totals */}
      <div>
        <h2 className="font-heading text-2xl font-semibold text-agave">Ingresos y comisiones</h2>
        <p className="text-sm text-neutral-600 mt-1">
          Resumen de pagos divididos automáticamente vía Stripe Connect Express. Rivones cobra el 15%, el anfitrión recibe el 85%.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-earnings-totals">
        <div className="card-base">
          <div className="text-xs text-neutral-500 uppercase tracking-wide">Reservas pagadas</div>
          <div className="text-2xl font-semibold text-agave mt-1">{totals.bookings}</div>
        </div>
        <div className="card-base">
          <div className="text-xs text-neutral-500 uppercase tracking-wide">Volumen bruto</div>
          <div className="text-2xl font-semibold text-agave mt-1">{fmtMxn(totals.gross_mxn)}</div>
        </div>
        <div className="card-base">
          <div className="text-xs text-neutral-500 uppercase tracking-wide">Comisión Rivones</div>
          <div className="text-2xl font-semibold text-terracotta mt-1">{fmtMxn(totals.fees_mxn)}</div>
        </div>
        <div className="card-base">
          <div className="text-xs text-neutral-500 uppercase tracking-wide">Pagos a hosts</div>
          <div className="text-2xl font-semibold text-emerald-700 mt-1">{fmtMxn(totals.payouts_mxn)}</div>
        </div>
      </div>

      {/* Earnings table */}
      <div className="card-base">
        <div className="flex items-center gap-2 mb-4">
          <Banknote className="w-5 h-5 text-terracotta" />
          <h3 className="font-semibold text-agave">Reservas recientes</h3>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-neutral-500 text-center py-8">
            Aún no hay reservas pagadas. Cuando un huésped complete un pago, aparecerá aquí con su división 85/15.
          </p>
        ) : (
          <div className="overflow-x-auto -mx-4 px-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] text-neutral-500 uppercase tracking-wide text-left border-b border-neutral-200">
                  <th className="py-2 font-medium">Reserva</th>
                  <th className="py-2 font-medium">Pagado</th>
                  <th className="py-2 font-medium text-right">Total</th>
                  <th className="py-2 font-medium text-right">Rivones (15%)</th>
                  <th className="py-2 font-medium text-right">Host (85%)</th>
                  <th className="py-2 font-medium text-right">Estado</th>
                </tr>
              </thead>
              <tbody>
                {items.map((b) => (
                  <tr key={b.booking_id} className="border-b border-neutral-100">
                    <td className="py-2.5 font-mono text-[12px] text-neutral-700">{b.booking_id?.slice(0, 14)}…</td>
                    <td className="py-2.5 text-neutral-600">{fmtDate(b.paid_at)}</td>
                    <td className="py-2.5 text-right">{fmtMxn(b.gross_mxn)}</td>
                    <td className="py-2.5 text-right text-terracotta">{fmtMxn(b.fee_mxn)}</td>
                    <td className="py-2.5 text-right text-emerald-700">{fmtMxn(b.payout_mxn)}</td>
                    <td className="py-2.5 text-right">
                      <PayoutPill status={b.payout_status} paymentStatus={b.payment_status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Manual review queue */}
      <div className="card-base">
        <div className="flex items-center gap-2 mb-4">
          <ShieldAlert className="w-5 h-5 text-amber-700" />
          <h3 className="font-semibold text-agave">Revisión manual de identidad</h3>
          {reviewItems.length > 0 && (
            <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold">
              {reviewItems.length}
            </span>
          )}
        </div>
        {reviewItems.length === 0 ? (
          <p className="text-sm text-neutral-500 text-center py-8">
            Sin verificaciones fallidas pendientes. Todas las identidades activas pasaron Stripe sin intervención.
          </p>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {reviewItems.map((r) => (
              <li key={r.user_id} className="py-3 flex flex-wrap items-start justify-between gap-3" data-testid={`review-row-${r.user_id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-sm text-agave truncate">
                    {r.user?.name || r.user_id}{" "}
                    <span className="text-neutral-500 font-normal">({r.user?.email})</span>
                  </p>
                  <p className="text-xs text-neutral-600 mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <AlertCircle className="w-3.5 h-3.5 text-amber-700" />
                    Stripe: <span className="font-mono">{r.code || r.status || "unknown"}</span>
                    {r.reason ? <> — <span className="text-neutral-500">{r.reason}</span></> : null}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => resolve(r.user_id, "approve")}
                    disabled={resolving === r.user_id + "approve"}
                    data-testid={`review-approve-${r.user_id}`}
                    className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                  >
                    {resolving === r.user_id + "approve" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                    Aprobar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => resolve(r.user_id, "block")}
                    disabled={resolving === r.user_id + "block"}
                    data-testid={`review-block-${r.user_id}`}
                    className="border-rose-300 text-rose-800 hover:bg-rose-50"
                  >
                    Bloquear
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
